/*
 * pogi-buy-box-compostable — §5.1a, ported verbatim from the BuyBox component in
 * pdp-compostable-components.jsx. Axes = FORMAT × COUNT (unscented only). The
 * embedded ladder model (formats→counts) is the RENDER source of truth; selecting
 * a (format, count) maps onto the real Shopify variant by option value to drive
 * Horizon's hidden native variant-picker + form (cart, selling plan stay native).
 * Subscribe discount uses the live selling plan when present, else the configured
 * fallback %. Review count (Judge.me) stays dynamic via Liquid.
 *
 * Role eyebrow per count: explicit `tag` (clay-deep) wins, else a per-label role,
 * else the count subtitle — so no card is ever left blank (mirrors the JSX).
 */

/* i18n: strings come from the data blob's `i18n` object (Liquid renders them via
   `| t` from locales/*.json pogi.bb.*). EVERY read falls back to the exact English
   literal that used to be hardcoded, so a missing key can never change the EN site.
   [[var]] placeholders (not {{ }}) so Liquid's t-filter never eats them. */
/* Market-currency guard for JS-DERIVED secondary prices. The variant prices in the
   data blob are the shop BASE-currency cents (USD) — not presentment-converted — so
   formatting them in a non-USD market shows a wrong number (e.g. "¥12"). The real
   prices localize via Liquid `| money`. To never show a wrong derived figure off-USD
   we HIDE the derived per-unit ("¢/bag") + savings lines when the active market
   currency isn't USD (the shop base). On USD this is true → identical behavior. */
const SHOW_DERIVED = !(window.Shopify && Shopify.currency && Shopify.currency.active) || Shopify.currency.active === 'USD';

const pogiTpl = (I, k, fb, vars) => {
  let s = (I && I[k]) || fb;
  if (vars) for (const key of Object.keys(vars)) s = s.split('[[' + key + ']]').join(String(vars[key]));
  return s;
};

/* Concrete arrival window for the ship nudge ("money moves" #2, 2026-06-12).
   Same math as the cart page (4pm cutoff, business days, 2-4 biz-day window). */
const pogiArrives = (I) => {
  const M = pogiTpl(I, 'js_months', 'Jan,Feb,Mar,Apr,May,Jun,Jul,Aug,Sep,Oct,Nov,Dec').split(',');
  const we = (d) => d.getDay() === 0 || d.getDay() === 6;
  const nb = (d) => { const x = new Date(d); do { x.setDate(x.getDate() + 1); } while (we(x)); return x; };
  const ab = (s, n) => { const x = new Date(s); let a = 0; while (a < n) { x.setDate(x.getDate() + 1); if (!we(x)) a++; } return x; };
  const now = new Date();
  const cut = new Date(now); cut.setHours(16, 0, 0, 0);
  const ship = (!we(now) && now < cut) ? now : nb(now);
  const a = ab(ship, 2), b = ab(ship, 4);
  const fmt = (d) => pogiTpl(I, 'js_date', '[[mon]] [[d]]', { mon: M[d.getMonth()], d: d.getDate() });
  const fmtShort = (d) => pogiTpl(I, 'js_date_short', '[[d]]', { d: d.getDate() });
  const range = fmt(a) + pogiTpl(I, 'js_range_join', '–') + (a.getMonth() === b.getMonth() ? fmtShort(b) : fmt(b));
  return pogiTpl(I, 'js_arrives', 'Arrives [[range]].', { range });
};

/* Role per count card, keyed by the BAGS number (stable across locales — the
   labels are translated per locale, the bag counts never change). k = i18n key,
   t = the exact English fallback. */
const ROLE_BY_BAGS = {
  135: { k: 'onthego', t: 'On the go' },
  270: { k: 'popular', t: 'Most popular', tone: 'accent' },
  480: { k: 'value', t: 'Best value', tone: 'accent' },
  140: { k: 'everyday', t: 'Everyday' },
  300: { k: 'stockup', t: 'Stock up', tone: 'olive' },
  120: { k: 'everyday', t: 'Everyday' },
  250: { k: 'stockup', t: 'Stock up', tone: 'olive' },
};
// Per-format line glyph — depicts the physical FORMAT (roll stack / jumbo roll /
// tie-handle bag), not the packaging. Same set as pogi-buy-box-bags, keyed to the
// compostable format keys (rolls / bigroll / handle). All normalized to the same
// vertical extent so the row reads even. Lucide stroke style (currentColor).
const FORMAT_GLYPH = {
  // Circles are TANGENT (hexagonal packing: row offset = r·√3, neighbors = 2r) so the
  // rolls touch like a real stack — floating gaps read wrong (owner 2026-06-12).
  'rolls': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="4.9" cy="18.2" r="3.55"/><circle cx="12" cy="18.2" r="3.55"/><circle cx="19.1" cy="18.2" r="3.55"/><circle cx="8.45" cy="12.05" r="3.55"/><circle cx="15.55" cy="12.05" r="3.55"/><circle cx="12" cy="5.9" r="3.55"/><circle cx="4.9" cy="18.2" r="1"/><circle cx="12" cy="18.2" r="1"/><circle cx="19.1" cy="18.2" r="1"/><circle cx="8.45" cy="12.05" r="1"/><circle cx="15.55" cy="12.05" r="1"/><circle cx="12" cy="5.9" r="1"/></svg>',
  'bigroll': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="3.5" rx="5.8" ry="2"/><path d="M6.2 3.5v17a5.8 2 0 0 0 11.6 0v-17"/><ellipse cx="12" cy="3.5" rx="1.9" ry="0.7"/></svg>',
  'handle': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5.2 20L4.8 11.8Q4.7 11 5.6 11H7V3Q7 1.6 8.5 1.6Q10 1.6 10 3V8.2Q12 10 14 8.2V3Q14 1.6 15.5 1.6Q17 1.6 17 3V11H18.4Q19.3 11 19.2 11.8L18.8 20Q18.7 22.4 16.4 22.5H7.6Q5.3 22.4 5.2 20Z"/></svg>',
};

/* POGI L10N FIX (2026-07-23): selling-plan names are LOCALIZED per storefront
   locale (9 locales: en/ja/fr/de/es/it + nl/pl/pt queued). The old parsers were
   EN(+JA)-only, so every EU plan name parsed null and interval matching fell
   back to wrong plans off-EN. Unit-word alternations + a no-digit=every-1 branch
   (fr «tous les mois», de „jeden Monat", es «cada mes», pl „co miesiąc», pt
   «mensal»). Match order month→week→day MUST hold: PL week words (tydzień/
   tygodni) contain the day stems (dzie/dni). Trip-tested against all live plan
   names ×6 locales + nl/pl/pt synthetics. */
const POGI_FREQ_WORDS = {
  month: 'month|か月|ヶ月|mois|monat|maand|miesi|mensil|mensual|mensal|mese|mesi|meses|mês|mes\\b',
  week: 'week|weke|週間|semaine|woche|wochen|settiman|semanas|semana|tygod|tydzi|semanal|wekelijks',
  day: 'day|日ごと|jour|tage|tag\\b|giorn|día|dias|dag\\b|dagen|dzie|dni\\b|dia\\b',
};
const pogiParseFreq = (raw) => {
  const s = String(raw).toLowerCase().replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xFEE0));
  // Pass 1: explicit "N <unit>" wins; with several numeric clauses the LAST one is
  // the frequency ("1 month supply – delivery every 2 weeks" / "Vorrat für einen
  // Monat – Lieferung alle 2 Wochen" must both parse week:2, never month:1).
  // matchAll (not match, which only sees the FIRST per-unit hit) so SAME-unit
  // compounds also keep the last clause ("2 weeks starter – every 4 weeks" = week:4).
  let best = null;
  for (const u of ['month', 'week', 'day']) {
    for (const m of s.matchAll(new RegExp('(\\d+)\\s*(?:' + POGI_FREQ_WORDS[u] + ')', 'g'))) {
      if (!best || m.index > best.idx) best = { u: u, n: parseInt(m[1], 10), idx: m.index };
    }
  }
  if (best) return { u: best.u, n: best.n };
  // Pass 2: a bare unit word = every-1 (fr «tous les mois», de „jeden Monat", pl „co miesiąc").
  for (const u of ['month', 'week', 'day']) {
    if (new RegExp(POGI_FREQ_WORDS[u]).test(s)) return { u: u, n: 1 };
  }
  return null;
};

// A11Y (audit P3, 2026-07-23): the buy box's radio-group containers, for the
// roving-tabindex pattern. .pogi-bb__purchase and [data-pogi-freq-opts] hold
// role="radio" buttons without a radiogroup role in the Liquid, so they are
// matched explicitly. The sizing modal's radios are deliberately NOT covered —
// it owns its own DOM lifecycle. Selectors absent from a given clone are
// harmless (querySelectorAll/closest simply never match).
const POGI_ROVE_GROUPS = '[data-pogi-formats], [data-pogi-sizes], [data-pogi-tiles], [data-pogi-counts], .pogi-bb__purchase, [data-pogi-freq-opts]';
// Eligible = focusable-for-roving: not disabled, not [hidden], and rendered.
// offsetParent is the visibility check: it is null when the radio OR any
// ancestor is display:none (this clone hides whole panels via [hidden]) — one
// check covers both, and none of these radios are position:fixed (the one
// case offsetParent cannot see). Chosen over getComputedStyle for cost
// (Codex countersign #19, 2026-07-23).
const POGI_ROVE_ELIGIBLE = (b) => !b.disabled && !b.hidden && b.offsetParent !== null;

class PogiBuyBoxCompostable extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-pogi-model]');
    if (!dataEl) return;
    try { this.data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    this.I = this.data.i18n || {};
    this.t = (k, fb, vars) => pogiTpl(this.I, k, fb, vars);

    this.formats = this.data.formats || [];
    if (!this.formats.length) return;
    this.moneyFormat = this.data.moneyFormat || '${{amount}}';
    this.currency = this.data.currency || null; // presentment ISO for market-aware formatting
    // Zero-decimal currency (JPY/KRW…): the "¢/bag" anchor is meaningless — show
    // whole-currency per-unit instead. USD/EUR keep the existing ¢ path untouched.
    this.zeroDec = (() => { try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.currency }).resolvedOptions().maximumFractionDigits === 0; } catch (e) { return false; } })();
    this.shipThreshold = parseInt(this.dataset.shipThreshold, 10) || 2500;
    this.subPctFallback = (parseFloat(this.dataset.subscribePct) || 15) / 100;

    this.section = this.closest('.shopify-section') || document;
    this.nativePicker = this.section.querySelector('variant-picker');
    this.nativeForm = document.getElementById(this.dataset.formId);

    // Detect which native option position holds the format names vs the count
    // labels, so selections can drive the hidden native picker for add-to-cart.
    this.#detectOptionPositions();

    // Initial state — the Liquid-selected variant when resolvable (POGI FIX
    // 2026-07-23, audit L8-002: the box used to ignore data.selectedId and
    // highlight its own pick), else format[0] with its default count.
    const selV = (this.data.variants || []).find((v) => v.id === this.data.selectedId) || null;
    const selFC = this.#countForVariant(selV);
    this.format = (selFC && selFC.format) || this.formats[0];
    this.count = (selFC && selFC.count) || this.format.counts[1] || this.format.counts[0];
    this.freq = this.count.defaultFreq;
    this.subscribe = false; // one-time default (owner call 2026-06-11)
    this.qty = 1;

    this.#renderFormats();
    this.#ensureSellingPlanInput();
    this.#bind();
    this.#renderCounts();
    this.#renderFreqs();
    this.#driveNative(); // L8-002: sync the native picker to the initial selection
    this.update();
    this.#injectGalleryBadge();
    this.#fixPreviewLinks('bags');
  }

  // In preview (?preview_theme_id / ?view present), carry the preview context onto the
  // material-toggle link so compostable⇄bags navigation stays on the bespoke templates.
  // No-op on the live storefront — no preview params → links stay clean.
  #fixPreviewLinks(view) {
    let params;
    try { params = new URLSearchParams(location.search); } catch (e) { return; }
    const pt = params.get('preview_theme_id');
    if (!pt && !params.get('view')) return;
    this.querySelectorAll('a.pogi-bb__mat[href]').forEach((a) => {
      try {
        const u = new URL(a.getAttribute('href'), location.origin);
        u.searchParams.set('view', view);
        if (pt) u.searchParams.set('preview_theme_id', pt);
        a.setAttribute('href', u.pathname + u.search);
      } catch (e) { /* leave as-is */ }
    });
  }

  /* ---------- helpers ---------- */
  #esc(s) { return String(s).replace(/"/g, '&quot;').replace(/</g, '&lt;'); }
  // Display: show "18 Rolls" big + "270 Bags" as the subtitle so the long label can't
  // overflow the card; the full "18 Rolls (270 Bags)" stays the ATC match value.
  // Jumbo/Tie labels ("140 Bags") have no parenthetical → pass through unchanged.
  #countHead(label) { return String(label).split(/[（(]/)[0].trim() || String(label); }
  #countSub(c) { const m = String(c.label).match(/[（(]([^）)]+)[）)]/); return (c.subtitle && c.subtitle.length) ? c.subtitle : (m ? m[1].trim() : ''); }

  money(cents) {
    const amt = cents / 100;
    if (this.currency) {
      try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.currency }).format(amt); }
      catch (e) { /* unknown ISO — fall through */ }
    }
    const grp = (s) => s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return this.moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, grp(amt.toFixed(2)))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, grp(Math.round(amt).toString()))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, grp(amt.toFixed(2)))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, grp(Math.round(amt).toString()));
  }
  moneyWhole(cents) { return this.money(Math.round(cents / 100) * 100).replace(/[.,]00\b/, ''); }

  // MAIN price display. The ladder prices (count.price, in cents) are base-shop (USD), so
  // JS money() only renders the right NUMBER on USD. Off the base currency we instead show
  // the matching variant's Liquid `| money` string (true presentment ¥/€…), looked up by
  // (format name, count label). On USD, SHOW_DERIVED is true → exact JS money() of the
  // ladder cents, so the US string stays byte-identical. Falls back to ladder cents when
  // no matching variant (e.g. dev catalog) so a number always shows.
  // Width-fold so locale strings match option values regardless of （）/() width.
  #wnorm(s) { return String(s).replace(/（/g, '(').replace(/）/g, ')').replace(/　/g, ' ').trim(); }
  /* POGI FIX 2026-07-23 (audit L4-001): the model's display labels come from theme
     translations while variant options are registered translations — the two
     sources drifted in 32 EU label pairs (NBSP vs space etc.), so label-equality
     matching failed per locale. The locale-proof contract: every count has a
     globally UNIQUE bags number (135/270/480/140/300/120/250, and roll counts
     9/18/32 collide with none of them), and digits survive every locale — a
     variant belongs to a count iff the count's bags number appears among the
     numbers of one of its option values. Labels are display-only from here on. */
  #numsIn(s) { return (String(s == null ? '' : s).match(/\d[\d,]*/g) || []).map((n) => parseInt(n.replace(/,/g, ''), 10)); }
  #variantForCount(count) {
    if (!count) return null;
    return (this.data.variants || []).find((v) => (v.options || []).some((o) => this.#numsIn(o).includes(count.bags))) || null;
  }
  #countForVariant(v) {
    if (!v) return null;
    const nums = (v.options || []).flatMap((o) => this.#numsIn(o));
    for (const f of this.formats) for (const c of f.counts) {
      if (nums.includes(c.bags)) return { format: f, count: c };
    }
    return null;
  }
  priceMoneyFor(formatName, count) {
    if (!SHOW_DERIVED) { const v = this.#variantForCount(count); if (v && v.money) return v.money; }
    return this.money(count.price);
  }

  fromPrice(f) { return Math.min.apply(null, f.counts.map((c) => c.price)); }
  // "from" string for a format: the cheapest count. USD → JS money(min cents); off base
  // currency → that count's matching variant presentment string.
  fromMoney(f) {
    const cheap = f.counts.reduce((a, b) => (b.price < a.price ? b : a));
    return this.priceMoneyFor(f.name, cheap);
  }
  months(bags) { return Math.max(1, Math.round((bags / 2) / 30)); }
  // Lowest price-per-bag count in a format (the SAVE anchor).
  cheapest(f) {
    return f.counts.reduce((a, b) => ((b.price / b.bags) < (a.price / a.bags) ? b : a));
  }

  /* ---------- native variant mapping ---------- */
  #detectOptionPositions() {
    // POGI FIX 2026-07-23 (L4-001): detect by DIGITS, not label equality — the
    // count axis is the option position whose values carry the known bags
    // numbers; the other position is the format axis. Locale-proof.
    this.formatPos = -1; this.countPos = -1;
    const variants = this.data.variants || [];
    if (!variants.length) return;
    const bagNums = new Set();
    this.formats.forEach((f) => f.counts.forEach((c) => bagNums.add(c.bags)));
    const nOpts = variants[0].options.length;
    for (let p = 0; p < nOpts; p++) {
      const hits = variants.filter((v) => this.#numsIn(v.options[p]).some((n) => bagNums.has(n))).length;
      if (hits > 0 && this.countPos < 0) this.countPos = p;
    }
    for (let p = 0; p < nOpts; p++) if (p !== this.countPos && this.formatPos < 0) this.formatPos = p;
  }
  #matchedVariant() {
    return this.#variantForCount(this.count); // numeric contract (L4-001) — labels are display-only
  }
  #setNativeOption(fieldsetIndex, value) {
    if (!this.nativePicker || fieldsetIndex < 0) return;
    const inputs = this.nativePicker.querySelectorAll(`input[data-fieldset-index="${fieldsetIndex}"]`);
    for (const input of inputs) {
      if (this.#wnorm(input.value) === this.#wnorm(value) && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
    }
  }
  #driveNative() {
    // Drive with the MATCHED VARIANT's own option strings (same source the native
    // inputs render), never the theme-translated model labels (L4-001 drift).
    const v = this.#matchedVariant();
    if (v) {
      this.#setNativeOption(this.formatPos, v.options[this.formatPos]);
      this.#setNativeOption(this.countPos, v.options[this.countPos]);
    } else {
      this.#setNativeOption(this.formatPos, this.format.name);
      this.#setNativeOption(this.countPos, this.count.label);
    }
  }

  /* ---------- selling plan ---------- */
  plansList() { const sp = this.data.sellingPlan; return (sp && sp.plans) || []; }
  // The live plan matching the chosen interval (by month count), else the first.
  currentPlan() {
    const plans = this.plansList();
    if (!plans.length) return null;
    const wantMo = Math.round(this.freq / 30);
    const match = plans.find((p) => {
      const pi = pogiParseFreq(p.name); // locale-proof (POGI L10N FIX 2026-07-23)
      if (!pi) return false;
      if (pi.u === 'month') return pi.n === wantMo;
      if (pi.u === 'week') return Math.round(pi.n / 4) === wantMo;
      return false;
    });
    // No cadence match -> keep plans[0]: a subscription at a correctable cadence beats
    // silently adding as one-time (the planInput caller drops the plan when null while
    // the UI still says Subscribe). Warn once so probes/audits can catch future naming.
    if (!match && !this._warnedPlanFallback) {
      this._warnedPlanFallback = true;
      console.warn('[pogi-buy-box] no selling plan matched interval; falling back to first plan');
    }
    return match || plans[0];
  }
  discountFrac() {
    if (!this.subscribe) return 0;
    const p = this.currentPlan();
    if (!p) return this.subPctFallback; // no live plan in dev — use configured fallback
    if (p.adjType === 'percentage') return p.adjValue / 100;
    if (p.adjType === 'fixed_amount') return Math.min(1, p.adjValue / this.count.price);
    if (p.adjType === 'price') return Math.max(0, 1 - p.adjValue / this.count.price);
    return this.subPctFallback;
  }

  /* ---------- rendering ---------- */
  #renderFormats() {
    const wrap = this.querySelector('[data-pogi-formats]');
    if (!wrap) return;
    wrap.innerHTML = this.formats.map((f) => {
      const glyph = FORMAT_GLYPH[f.key] || '';
      return `
      <button type="button" class="pogi-bb__format" role="radio" data-pogi-format="${this.#esc(f.key)}" aria-checked="false">
        ${glyph ? `<span class="pogi-bb__format-glyph" aria-hidden="true">${glyph}</span>` : ''}
        <span class="pogi-bb__format-name">${this.#esc(f.name)}</span>
        <span class="pogi-bb__format-desc">${this.#esc(f.desc)}</span>
        <span class="pogi-bb__format-from">${this.t('js_from', 'from [[price]]', { price: this.fromMoney(f) })}</span>
      </button>`;
    }).join('');
  }

  #renderCounts() {
    const wrap = this.querySelector('[data-pogi-counts]');
    if (!wrap) return;
    const counts = this.format.counts;
    wrap.setAttribute('data-cols', String(counts.length === 3 ? 3 : (counts.length === 1 ? 1 : 2)));
    const cheap = this.cheapest(this.format);
    wrap.innerHTML = counts.map((c) => {
      const role = c.tag
        ? { t: c.tag, cls: 'pogi-bb__count-role--accent' }
        : (() => { const r = ROLE_BY_BAGS[c.bags] || { t: c.subtitle }; return { t: r.k ? this.t('js_role_' + r.k, r.t) : r.t, cls: r.tone === 'accent' ? 'pogi-bb__count-role--accent' : (r.tone === 'olive' ? 'pogi-bb__count-role--olive' : '') }; })();
      const perLine = (!c.bags || !SHOW_DERIVED) ? null : this.zeroDec
        ? this.t('js_per_unit_short_money', '[[money]]/[[unit]]', { money: this.money(Math.round(c.price / c.bags)), unit: this.t('unit_bag', 'bag') })
        : this.t('js_per_unit_short', '[[cents]]¢/[[unit]]', { cents: (c.price / c.bags).toFixed(1), unit: this.t('unit_bag', 'bag') }); // price is already cents → ¢/bag
      const save = (SHOW_DERIVED && c.bags && c !== cheap) ? Math.round((cheap.price / cheap.bags) * c.bags - c.price) : 0;
      return `
      <button type="button" class="pogi-bb__count" role="radio" data-pogi-count="${this.#esc(c.label)}" aria-checked="false">
        <span class="pogi-bb__count-top">
          <span class="pogi-bb__count-role ${role.cls}">${this.#esc(role.t)}</span>
          <span class="pogi-bb__count-check" aria-hidden="true">✓</span>
        </span>
        <span class="pogi-bb__count-mid">
          <span class="pogi-bb__count-label">${this.#esc(this.#countHead(c.label))}</span>
          <span class="pogi-bb__count-price">${this.priceMoneyFor(this.format.name, c)}</span>
        </span>
        <span class="pogi-bb__count-bot">
          <span class="pogi-bb__count-sub">${this.#esc(this.#countSub(c))}</span>
          <span class="pogi-bb__count-anchor">
            ${perLine ? `<span class="pogi-bb__count-per">${perLine}</span>` : ''}
            ${save > 0 ? `<span class="pogi-bb__count-save">${this.t('js_save_caps', 'SAVE [[amount]]', { amount: this.moneyWhole(save) })}</span>` : ''}
          </span>
        </span>
      </button>`;
    }).join('');
  }

  #renderFreqs() {
    const wrap = this.querySelector('[data-pogi-freq-opts]');
    if (!wrap) return;
    const freqs = this.count.freqs || [30, 45, 60, 90];
    wrap.innerHTML = freqs.map((d) => `
      <button type="button" class="pogi-bb__freq-opt" role="radio" data-pogi-freq="${d}" aria-checked="${d === this.freq}">${this.t('js_freq_mo', '[[n]] mo', { n: Math.round(d / 30) })}</button>`).join('');
  }

  /* ---------- events ---------- */
  #bind() {
    this.addEventListener('click', (e) => {
      const fmt = e.target.closest('[data-pogi-format]');
      const cnt = e.target.closest('[data-pogi-count]');
      const mode = e.target.closest('[data-pogi-mode]');
      const freq = e.target.closest('[data-pogi-freq]');
      const dt = e.target.closest('[data-pogi-desc-toggle]');
      const dec = e.target.closest('[data-pogi-qty-dec]');
      const inc = e.target.closest('[data-pogi-qty-inc]');
      if (fmt) { this.#setFormat(fmt.dataset.pogiFormat); }
      else if (cnt) { this.#setCount(cnt.dataset.pogiCount); }
      else if (mode) { this.subscribe = mode.dataset.pogiMode === 'subscribe'; this.update(); }
      else if (freq) { this.freq = parseInt(freq.dataset.pogiFreq, 10); this.#renderFreqs(); this.update(); }
      else if (dt) { const d = this.querySelector('[data-pogi-desc]'); const o = d.classList.toggle('is-open'); dt.textContent = o ? this.t('read_less', 'Read less') : this.t('read_more', 'Read more'); dt.setAttribute('aria-expanded', String(o)); }
      else if (dec) { this.qty = Math.max(1, this.qty - 1); this.#syncQty(); }
      else if (inc) { this.qty += 1; this.#syncQty(); }
    });
    // A11Y (audit P3, 2026-07-23): ONE delegated keydown per component root —
    // roving-tabindex arrow-key support for the radio groups (see #roveKey).
    this.addEventListener('keydown', (e) => this.#roveKey(e));
  }

  /* ---- a11y: roving tabindex (audit P3 + countersign #19, 2026-07-23) ---- */
  // ONE tab stop per radio group: tabindex 0 on the checked ELIGIBLE radio
  // (first eligible if none), -1 on every other radio — hidden/disabled ones
  // included, so they can never hold a stale tab stop. Called at the END of
  // update(), AFTER the [hidden] toggles, so eligibility is judged on the
  // pass's final visibility state. Behavior-additive: click handling is
  // untouched.
  #rove() {
    this.querySelectorAll(POGI_ROVE_GROUPS).forEach((g) => {
      const all = [...g.querySelectorAll('[role="radio"]')];
      const radios = all.filter(POGI_ROVE_ELIGIBLE);
      all.forEach((b) => b.setAttribute('tabindex', '-1')); // hidden/disabled are never the tab stop
      if (!radios.length) return;
      const on = radios.find((b) => b.getAttribute('aria-checked') === 'true') || radios[0];
      on.setAttribute('tabindex', '0'); // checked-and-eligible, else first ELIGIBLE
    });
  }
  // Arrow keys move focus AND activate the next/prev radio with wrap-around;
  // Home/End jump to first/last. Activation goes through .click() — the exact
  // path pointer input takes — so selection, native-picker drive and update()
  // all reuse the existing delegated click handler above.
  #roveKey(e) {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) return;
    const radio = e.target.closest('[role="radio"]');
    const group = radio && radio.closest(POGI_ROVE_GROUPS);
    if (!group) return; // not one of our groups (e.g. the sizing modal) — leave the key alone
    const radios = [...group.querySelectorAll('[role="radio"]')].filter(POGI_ROVE_ELIGIBLE); // arrows skip hidden/disabled radios
    const i = radios.indexOf(radio);
    if (i < 0) return;
    e.preventDefault();
    const next = e.key === 'Home' ? radios[0]
      : e.key === 'End' ? radios[radios.length - 1]
        : (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? radios[(i + 1) % radios.length]
          : radios[(i - 1 + radios.length) % radios.length];
    if (next === radio) return;
    next.focus();
    next.click();
    // A click can rebuild the clicked group's innerHTML (the freq group here
    // re-renders on selection) — re-focus the fresh tab stop so keyboard context
    // is never dropped mid-interaction.
    if (!next.isConnected) { const nb = group.querySelector('[role="radio"][tabindex="0"]'); if (nb) nb.focus(); }
  }

  // Changing format resets to that format's default count (+ its frequency).
  #setFormat(key) {
    const f = this.formats.find((x) => x.key === key);
    if (!f) return;
    this.format = f;
    this.count = f.counts[1] || f.counts[0];
    if (this.count.defaultFreq) this.freq = this.count.defaultFreq;
    this.#renderCounts();
    this.#renderFreqs();
    this.#driveNative();
    this.update();
  }
  // Changing count snaps the delivery frequency to that count's default.
  #setCount(label) {
    const c = this.format.counts.find((x) => x.label === label);
    if (!c) return;
    this.count = c;
    if (c.defaultFreq) this.freq = c.defaultFreq;
    this.#renderFreqs();
    this.#driveNative();
    this.update();
  }

  update() {
    // Selected states
    this.querySelectorAll('[data-pogi-format]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.pogiFormat === this.format.key)));
    this.querySelectorAll('[data-pogi-count]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.pogiCount === this.count.label)));
    this.querySelectorAll('[data-pogi-mode]').forEach((b) => b.setAttribute('aria-checked', String((b.dataset.pogiMode === 'subscribe') === this.subscribe)));
    this.querySelectorAll('[data-pogi-freq]').forEach((b) => b.setAttribute('aria-checked', String(parseInt(b.dataset.pogiFreq, 10) === this.freq)));

    const fl = this.querySelector('[data-pogi-format-label]');
    if (fl) fl.textContent = this.format.name;

    // Count estimate eyebrow — "{label} ({bags} bags) lasts about {N} months"
    const cest = this.querySelector('[data-pogi-cest]');
    if (cest) {
      const mo = this.months(this.count.bags);
      // bag count alone ("270 Bags") when the label carries a parenthetical — one-line fit on mobile
      const shortLabel = (this.count.label.match(/[（(]([^）)]+)[）)]/) || [])[1] || this.count.label;
      const dur = this.t('js_dur', '[[n]] [[unit]]', { n: mo, unit: this.t(mo === 1 ? 'js_unit_month' : 'js_unit_months', mo === 1 ? 'month' : 'months') });
      cest.innerHTML = `<strong>${this.#esc(shortLabel)}</strong> ${this.t('lasts_about', 'lasts about')} <span style="white-space:nowrap"><strong>${dur}</strong> ` +
        `<button type="button" class="pogi-bb__hint" aria-label="${this.#esc(this.t('hint_aria', 'How is this estimate calculated?'))}" title="${this.#esc(this.t('hint_title_compostable', 'Based on one dog, twice-daily walks'))}">?</button></span>`;
    }

    const base = this.count.price;
    const frac = this.discountFrac();
    const final = this.subscribe ? Math.round(base * (1 - frac)) : base;
    const total = final * this.qty;

    const setT = (sel, val) => { const el = this.querySelector(sel); if (el) el.textContent = val; };
    // Sub card always shows the discounted price + struck base (independent of the
    // currently selected mode). Live plan % wins; else the configured fallback.
    const livePlan = this.currentPlan();
    const subFrac = (livePlan && livePlan.adjType === 'percentage') ? livePlan.adjValue / 100 : this.subPctFallback;
    // On USD: exact JS money() (byte-identical). Off the base currency: the subscribe figure
    // is a JS-derived discount we can't presentment-convert, so fall back to the count's true
    // presentment base price (better than a wrong USD-cents number). The struck "was" is
    // already hidden off-USD.
    const otMoney = this.priceMoneyFor(this.format.name, this.count);
    setT('[data-pogi-sub-price]', SHOW_DERIVED ? this.money(Math.round(base * (1 - subFrac))) : otMoney);
    setT('[data-pogi-sub-was]', SHOW_DERIVED ? this.money(base) : '');
    setT('[data-pogi-ot-price]', otMoney);
    const badge = this.querySelector('[data-pogi-save-badge]');
    if (badge) badge.textContent = (SHOW_DERIVED && subFrac > 0) ? this.t('js_save', 'Save [[amount]]', { amount: this.money(Math.round(base * subFrac)) }) : '';

    // Subscribe expander
    const subx = this.querySelector('[data-pogi-subx]');
    if (subx) subx.hidden = !this.subscribe;
    const subxDesc = this.querySelector('[data-pogi-subx-desc]');
    if (subxDesc) {
      const mo = this.months(this.count.bags);
      const shortLabel = (this.count.label.match(/[（(]([^）)]+)[）)]/) || [])[1] || this.count.label;
      const dur = this.t('js_dur', '[[n]] [[unit]]', { n: mo, unit: this.t(mo === 1 ? 'js_unit_month' : 'js_unit_months', mo === 1 ? 'month' : 'months') });
      subxDesc.textContent = this.t('js_subx_desc_box', '[[pack]] lasts about [[dur]]. We send your next box just before you run low.', { pack: shortLabel, dur });
    }

    // Native selling plan + ATC
    this.#ensureSellingPlanInput();
    const planInput = this.nativeForm && this.nativeForm.querySelector('input[name="selling_plan"]');
    const plan = this.currentPlan();
    if (planInput) planInput.value = (this.subscribe && plan) ? plan.id : '';
    // POGI FIX 2026-07-23 (L8-002): write the matched variant id synchronously —
    // the native picker drive is async (morph), so an immediate ATC could submit
    // the stale variant. Mirrors the other buy-box clones.
    const idInput = this.nativeForm && this.nativeForm.querySelector('input[name="id"]');
    const matchedForId = this.#matchedVariant();
    if (idInput && matchedForId) { idInput.value = matchedForId.id; idInput.dataset.pogiSync = '1'; } // POGI: marks this form synchronously managed (product-form #getIntendedVariantId)

    const matched = this.#matchedVariant();
    const atc = this.querySelector('[data-pogi-atc]');
    const atcLabel = this.querySelector('[data-pogi-atc-label]');
    const available = matched ? matched.available : true;
    if (atc) {
      atc.disabled = !available;
      // USD → exact JS money() of the (qty-multiplied, possibly discounted) total. Off the
      // base currency that math can't be presentment-converted → show the count's true
      // presentment base price (qty 1 / undiscounted) rather than a wrong USD-cents number.
      const atcMoney = SHOW_DERIVED ? this.money(total) : this.priceMoneyFor(this.format.name, this.count);
      atcLabel.textContent = available
        ? (this.subscribe ? this.t('js_atc_sub', 'Start my routine · ') : this.t('js_atc_add', 'Add to cart · ')) + atcMoney
        : this.t('js_sold_out', 'Sold out');
    }

    // Free-shipping nudge — subscriptions always ship free; one-time counts toward the threshold.
    // (When not subscribed, `total` === base × qty, i.e. the full one-time total.)
    const ship = this.querySelector('[data-pogi-ship]');
    if (ship) {
      const msg = ship.querySelector('[data-pogi-ship-msg]');
      const fill = ship.querySelector('[data-pogi-ship-fill]');
      if (this.subscribe) {
        ship.classList.add('is-unlocked');
        if (msg) msg.innerHTML = '<span style="white-space:nowrap">' + this.t('js_ship_always_html', '<strong>Free shipping, always.</strong>') + '</span> <span style="white-space:nowrap">' + pogiArrives(this.I) + '</span>';
        if (fill) fill.style.width = '100%';
      } else {
        const remaining = Math.max(0, this.shipThreshold - total);
        if (remaining === 0) {
          ship.classList.add('is-unlocked');
          if (msg) msg.innerHTML = '<span style="white-space:nowrap">' + this.t('js_ship_unlocked_html', '<strong>Free shipping unlocked.</strong>') + '</span> <span style="white-space:nowrap">' + pogiArrives(this.I) + '</span>';
        } else {
          ship.classList.add('is-unlocked');
          if (msg) msg.innerHTML = this.t('js_ship_remaining_html', "You're <strong>[[amount]]</strong> from free shipping.", { amount: this.money(remaining) });
        }
        if (fill) fill.style.width = '100%';
      }
    }
    // Roving tabindex LAST — after the [hidden] panel toggles above, so a
    // hidden/disabled radio is never left holding a group's tab stop
    // (Codex countersign #19, 2026-07-23).
    this.#rove();
  }

  #ensureSellingPlanInput() {
    if (!this.nativeForm) return;
    if (!this.nativeForm.querySelector('input[name="selling_plan"]')) {
      const i = document.createElement('input'); i.type = 'hidden'; i.name = 'selling_plan'; this.nativeForm.appendChild(i);
    }
  }
  #syncQty() {
    const out = this.querySelector('[data-pogi-qty]');
    if (out) out.textContent = this.qty;
    const nq = this.nativeForm && this.nativeForm.querySelector('input[name="quantity"]');
    if (nq) { nq.value = this.qty; nq.dispatchEvent(new Event('change', { bubbles: true })); }
    this.update();
  }

  #injectGalleryBadge() {
    const text = (this.dataset.galleryBadge || '').trim();
    if (!text) return;
    const tile = this.section.querySelector('.product-media-container');
    if (!tile || tile.querySelector('.pogi-gallery-badge')) return;
    const chip = document.createElement('span');
    chip.className = 'pogi-gallery-badge';
    chip.textContent = text;
    tile.appendChild(chip);
  }
}

if (!customElements.get('pogi-buy-box-compostable')) {
  customElements.define('pogi-buy-box-compostable', PogiBuyBoxCompostable);
}
